import {
  Button,
  cn,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@newfront-insurance/core-ui/v2';
import { Pencil } from 'lucide-react';
import type { ReactNode } from 'react';

import {
  changeNovaScheduleRecurrencePreset,
  formatNovaScheduleRecurrence,
  fromNovaScheduleDateTimeInput,
  getNovaScheduleMinimumDateTimeInput,
  NOVA_SCHEDULE_WEEKDAYS,
  toNovaScheduleDateTimeInput,
  type NovaScheduleOutputPolicy,
  type NovaScheduleProposalDraft,
  type NovaScheduleRecurrence,
  type NovaScheduleRecurrencePreset,
  type NovaScheduleWeekday,
} from '../../../../lib/nova-schedule-proposal';
import { novaGradientClass } from '../../design-system/gradients';

const RECURRENCE_LABELS: Record<NovaScheduleRecurrencePreset, string> = {
  ONCE: 'One time',
  HOURLY: 'Hourly',
  DAILY: 'Daily',
  WEEKDAYS: 'Weekdays',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
};

const WEEKDAY_LABELS: Record<NovaScheduleWeekday, string> = {
  SU: 'S',
  MO: 'M',
  TU: 'T',
  WE: 'W',
  TH: 'T',
  FR: 'F',
  SA: 'S',
};

const WEEKDAY_NAMES: Record<NovaScheduleWeekday, string> = {
  SU: 'Sunday',
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
};

interface NovaScheduleProposalFieldsProps {
  draft: NovaScheduleProposalDraft;
  disabled?: boolean;
  accountLabel: string;
  firstRunLabel?: string;
  onEditAccount?: () => void;
  onEditDelivery: () => void;
  onEditWhen: () => void;
}

export function NovaScheduleProposalFields({
  draft,
  disabled,
  accountLabel,
  firstRunLabel,
  onEditAccount,
  onEditDelivery,
  onEditWhen,
}: NovaScheduleProposalFieldsProps) {
  return (
    <div className="flex flex-col gap-4">
      <ScheduleEditTrigger
        label="Account"
        value={accountLabel}
        disabled={disabled}
        highlighted={Boolean(onEditAccount)}
        onEdit={onEditAccount}
      />

      <div className="flex flex-col gap-[26px]">
        <ScheduleEditTrigger
          label="When"
          value={formatNovaScheduleRecurrence(draft.recurrence)}
          disabled={disabled}
          onEdit={onEditWhen}
        />

        {draft.recurrence.preset !== 'ONCE' && (
          <ScheduleEditTrigger
            label="Delivery"
            value={formatDeliveryPolicy(draft.outputPolicy)}
            disabled={disabled}
            onEdit={onEditDelivery}
          />
        )}

        {firstRunLabel ? <ScheduleSummaryValue label="First run" value={firstRunLabel} /> : null}
      </div>
    </div>
  );
}

function ScheduleEditTrigger({
  label,
  value,
  disabled,
  highlighted = false,
  onEdit,
}: {
  label: string;
  value: string;
  disabled?: boolean;
  highlighted?: boolean;
  onEdit?: () => void;
}) {
  const content = (
    <>
      <span className="text-steel-500 w-[50px] shrink-0 text-[12px] leading-4 font-semibold">{label}</span>
      <span
        className={cn(
          'text-steel-400 flex min-w-0 flex-1 items-center gap-1 rounded-[33px] text-[12px] leading-4 transition-colors',
          highlighted ? 'bg-wtw-infinity-50 px-4 py-[10px]' : '-my-[10px] -ml-4 px-4 py-[10px]',
          !highlighted && onEdit && 'group-hover:bg-wtw-infinity-50 group-focus-visible:bg-wtw-infinity-50',
        )}
      >
        <span className="min-w-0 flex-1 truncate">{value}</span>
        {onEdit && (
          <Pencil
            aria-hidden="true"
            className={cn(
              'text-steel-400 size-4 shrink-0 transition-opacity',
              highlighted ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
            )}
          />
        )}
      </span>
    </>
  );

  if (!onEdit) {
    return <div className={cn('flex w-full items-center', highlighted ? 'gap-6' : 'gap-10')}>{content}</div>;
  }

  return (
    <button
      type="button"
      aria-label={`Edit ${label.toLowerCase()}`}
      disabled={disabled}
      onClick={onEdit}
      className={cn(
        'group focus-visible:ring-brand-400 flex w-full items-center text-left focus-visible:ring-2 focus-visible:outline-hidden',
        'disabled:cursor-not-allowed disabled:opacity-60',
        highlighted ? 'gap-6' : 'gap-10',
      )}
    >
      {content}
    </button>
  );
}

function ScheduleSummaryValue({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex w-full items-center gap-10">
      <span className="text-steel-500 w-[50px] shrink-0 text-[12px] leading-4 font-semibold">{label}</span>
      <span className="text-steel-400 min-w-0 flex-1 truncate text-[12px] leading-4">{value}</span>
    </div>
  );
}

export function NovaScheduleWhenEditor({
  draft,
  timezone,
  disabled,
  onChange,
}: {
  draft: NovaScheduleProposalDraft;
  timezone: string;
  disabled?: boolean;
  onChange: (draft: NovaScheduleProposalDraft) => void;
}) {
  const updateDraft = (updates: Partial<NovaScheduleProposalDraft>) => onChange({ ...draft, ...updates });

  return (
    <WhenEditor
      recurrence={draft.recurrence}
      startAt={draft.startAt}
      timezone={timezone}
      disabled={disabled}
      onPresetChange={(preset) =>
        updateDraft({
          recurrence: changeNovaScheduleRecurrencePreset(draft.recurrence, preset),
          outputPolicy: preset === 'ONCE' ? undefined : (draft.outputPolicy ?? 'APPEND_TO_CHAT'),
        })
      }
      onStartAtChange={(startAt) => updateDraft({ startAt })}
      onChange={(recurrence) => updateDraft({ recurrence })}
    />
  );
}

const DELIVERY_OPTIONS: Array<{
  value: NovaScheduleOutputPolicy;
  label: string;
  description: string;
}> = [
  {
    value: 'NEW_CHAT_PER_RUN',
    label: 'Fresh chat thread',
    description: 'A fresh thread every time it runs. Best for periodic prep you handle then move on.',
  },
  {
    value: 'APPEND_TO_CHAT',
    label: 'Continuous chat thread',
    description: 'Every run adds to the same chat. Best for an ongoing log you scroll back through.',
  },
];

export function NovaScheduleDeliveryEditor({
  value,
  disabled,
  onChange,
}: {
  value: NovaScheduleOutputPolicy | undefined;
  disabled?: boolean;
  onChange: (value: NovaScheduleOutputPolicy) => void;
}) {
  const selectedValue = value ?? 'APPEND_TO_CHAT';

  return (
    <RadioGroup
      value={selectedValue}
      aria-label="Delivery"
      className="flex flex-col gap-[3px]"
      onValueChange={(nextValue) => onChange(nextValue as NovaScheduleOutputPolicy)}
      disabled={disabled}
    >
      {DELIVERY_OPTIONS.map((option) => (
        <label
          key={option.value}
          className={cn(
            'hover:bg-wtw-infinity-50 focus-within:bg-wtw-infinity-50 flex cursor-pointer items-start gap-3 rounded-[20px] px-4 py-[10px] transition-colors',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          <RadioGroupItem value={option.value} className="mt-px size-[18px]" />
          <span className="text-steel-400 text-[14px] leading-5">{`${option.label}: ${option.description}`}</span>
        </label>
      ))}
    </RadioGroup>
  );
}

function WhenEditor({
  recurrence,
  startAt,
  timezone,
  disabled,
  onPresetChange,
  onStartAtChange,
  onChange,
}: {
  recurrence: NovaScheduleRecurrence;
  startAt?: string;
  timezone: string;
  disabled?: boolean;
  onPresetChange: (preset: NovaScheduleRecurrencePreset) => void;
  onStartAtChange: (startAt: string | undefined) => void;
  onChange: (recurrence: NovaScheduleRecurrence) => void;
}) {
  const sharedProps = { recurrence, disabled, onPresetChange, onChange };

  switch (recurrence.preset) {
    case 'ONCE':
      return (
        <OneTimeControls {...sharedProps} startAt={startAt} timezone={timezone} onStartAtChange={onStartAtChange} />
      );
    case 'HOURLY':
      return <HourlyControls {...sharedProps} />;
    case 'DAILY':
    case 'WEEKDAYS':
      return <TimedRecurrenceControls {...sharedProps} />;
    case 'WEEKLY':
      return <WeeklyControls {...sharedProps} />;
    case 'MONTHLY':
      return <MonthlyControls {...sharedProps} />;
  }
}

interface RecurrenceControlsProps {
  recurrence: NovaScheduleRecurrence;
  disabled?: boolean;
  onPresetChange: (preset: NovaScheduleRecurrencePreset) => void;
  onChange: (recurrence: NovaScheduleRecurrence) => void;
}

function OneTimeControls({
  recurrence,
  startAt,
  timezone,
  disabled,
  onPresetChange,
  onStartAtChange,
}: RecurrenceControlsProps & {
  startAt?: string;
  timezone: string;
  onStartAtChange: (startAt: string | undefined) => void;
}) {
  return (
    <ScheduleControlRow>
      <RecurrenceSelect recurrence={recurrence} disabled={disabled} onChange={onPresetChange} />
      <div className="w-full min-w-[220px] flex-1 sm:max-w-[320px]">
        <Input
          fieldId="nova-schedule-start-at"
          aria-label="Reminder date and time"
          type="datetime-local"
          min={getNovaScheduleMinimumDateTimeInput(timezone)}
          value={toNovaScheduleDateTimeInput(startAt, timezone)}
          disabled={disabled}
          onChange={(event) => onStartAtChange(fromNovaScheduleDateTimeInput(event.target.value, timezone))}
          wrapperClassName="h-[42px] bg-white"
          inputClassName="text-[14px]"
        />
      </div>
    </ScheduleControlRow>
  );
}

function HourlyControls({ recurrence, disabled, onPresetChange, onChange }: RecurrenceControlsProps) {
  return (
    <ScheduleControlRow>
      <RecurrenceSelect recurrence={recurrence} disabled={disabled} onChange={onPresetChange} />
      <ScheduleControlLabel>at</ScheduleControlLabel>
      <MinuteOfHourPicker
        value={recurrence.minuteOfHour ?? 0}
        disabled={disabled}
        onChange={(minuteOfHour) => onChange({ ...recurrence, minuteOfHour })}
      />
      <ScheduleControlLabel>past the hour</ScheduleControlLabel>
    </ScheduleControlRow>
  );
}

function TimedRecurrenceControls({ recurrence, disabled, onPresetChange, onChange }: RecurrenceControlsProps) {
  return (
    <ScheduleControlRow>
      <RecurrenceSelect recurrence={recurrence} disabled={disabled} onChange={onPresetChange} />
      <ScheduleControlLabel>at</ScheduleControlLabel>
      <TimeOfDayPicker
        value={recurrence.timeOfDay ?? '09:00'}
        disabled={disabled}
        onChange={(timeOfDay) => onChange({ ...recurrence, timeOfDay })}
      />
    </ScheduleControlRow>
  );
}

function WeeklyControls({ recurrence, disabled, onPresetChange, onChange }: RecurrenceControlsProps) {
  return (
    <div className="flex flex-col gap-4">
      <ScheduleControlRow>
        <RecurrenceSelect recurrence={recurrence} disabled={disabled} onChange={onPresetChange} />
        <ScheduleControlLabel>on</ScheduleControlLabel>
        <WeekdayPicker
          weekdays={recurrence.weekdays ?? []}
          disabled={disabled}
          onChange={(weekdays) => onChange({ ...recurrence, weekdays })}
        />
      </ScheduleControlRow>
      <ScheduleControlRow>
        <ScheduleControlLabel className="sm:w-[13px]">at</ScheduleControlLabel>
        <TimeOfDayPicker
          value={recurrence.timeOfDay ?? '09:00'}
          disabled={disabled}
          onChange={(timeOfDay) => onChange({ ...recurrence, timeOfDay })}
        />
      </ScheduleControlRow>
    </div>
  );
}

function MonthlyControls({ recurrence, disabled, onPresetChange, onChange }: RecurrenceControlsProps) {
  return (
    <ScheduleControlRow>
      <RecurrenceSelect recurrence={recurrence} disabled={disabled} onChange={onPresetChange} />
      <ScheduleControlLabel>on the</ScheduleControlLabel>
      <DayOfMonthPicker
        value={recurrence.dayOfMonth ?? 1}
        disabled={disabled}
        onChange={(dayOfMonth) => onChange({ ...recurrence, dayOfMonth })}
      />
      <ScheduleControlLabel>at</ScheduleControlLabel>
      <TimeOfDayPicker
        value={recurrence.timeOfDay ?? '09:00'}
        disabled={disabled}
        onChange={(timeOfDay) => onChange({ ...recurrence, timeOfDay })}
      />
    </ScheduleControlRow>
  );
}

function ScheduleControlRow({ children }: { children: ReactNode }) {
  return <div className="flex min-w-0 flex-wrap items-center gap-x-6 gap-y-3">{children}</div>;
}

function RecurrenceSelect({
  recurrence,
  disabled,
  onChange,
}: {
  recurrence: NovaScheduleRecurrence;
  disabled?: boolean;
  onChange: (preset: NovaScheduleRecurrencePreset) => void;
}) {
  return (
    <div className="w-full sm:w-[179px]">
      <Select value={recurrence.preset} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger aria-label="Recurrence" className="h-[42px] bg-white text-[14px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(RECURRENCE_LABELS).map(([preset, label]) => (
            <SelectItem key={preset} value={preset}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ScheduleControlLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-steel-400 shrink-0 text-[12px] leading-4', className)}>{children}</span>;
}

type TimePeriod = 'AM' | 'PM';

interface ParsedTimeOfDay {
  clockValue: string;
  period: TimePeriod;
}

const HALF_HOUR_CLOCK_OPTIONS = Array.from({ length: 24 }, (_, index) => formatClockValue(index * 30));

function TimeOfDayPicker({
  value,
  disabled,
  onChange,
}: {
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  const { clockValue, period } = parseTimeOfDay(value);
  const clockOptions = HALF_HOUR_CLOCK_OPTIONS.includes(clockValue)
    ? HALF_HOUR_CLOCK_OPTIONS
    : [...HALF_HOUR_CLOCK_OPTIONS, clockValue].sort(
        (left, right) => clockValueToMinutes(left) - clockValueToMinutes(right),
      );

  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="w-[78px]">
        <Select
          value={clockValue}
          disabled={disabled}
          onValueChange={(nextClockValue) => onChange(toTwentyFourHourTime(nextClockValue, period))}
        >
          <SelectTrigger aria-label="Time of day" className="h-[42px] bg-white px-3 text-[14px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {clockOptions.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <RadioGroup
        value={period}
        aria-label="Time period"
        className="flex w-auto items-center gap-3"
        disabled={disabled}
        onValueChange={(nextPeriod) => onChange(toTwentyFourHourTime(clockValue, nextPeriod as TimePeriod))}
      >
        {(['AM', 'PM'] as const).map((option) => (
          <label key={option} className="text-steel-400 flex cursor-pointer items-center gap-1.5 text-[14px]">
            <RadioGroupItem value={option} className="size-[18px]" />
            {option}
          </label>
        ))}
      </RadioGroup>
    </div>
  );
}

function MinuteOfHourPicker({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className="w-[68px]">
      <Select value={String(value)} disabled={disabled} onValueChange={(nextValue) => onChange(Number(nextValue))}>
        <SelectTrigger aria-label="Minute past the hour" className="h-[42px] bg-white px-3 text-[14px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 60 }, (_, minute) => (
            <SelectItem key={minute} value={String(minute)}>
              {`:${String(minute).padStart(2, '0')}`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DayOfMonthPicker({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled?: boolean;
  onChange: (value: number) => void;
}) {
  return (
    <div className="w-[70px]">
      <Select value={String(value)} disabled={disabled} onValueChange={(nextValue) => onChange(Number(nextValue))}>
        <SelectTrigger aria-label="Day of month" className="h-[42px] bg-white px-3 text-[14px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
            <SelectItem key={day} value={String(day)}>
              {formatOrdinal(day)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function WeekdayPicker({
  weekdays,
  disabled,
  onChange,
}: {
  weekdays: NovaScheduleWeekday[];
  disabled?: boolean;
  onChange: (weekdays: NovaScheduleWeekday[]) => void;
}) {
  return (
    <div role="group" aria-label="Weekdays" className="flex flex-wrap gap-1.5">
      {NOVA_SCHEDULE_WEEKDAYS.map((weekday) => {
        const isSelected = weekdays.includes(weekday);

        return (
          <Button
            key={weekday}
            type="button"
            variant="transparent"
            size="small"
            aria-label={WEEKDAY_NAMES[weekday]}
            aria-pressed={isSelected}
            disabled={disabled}
            onClick={() => {
              const selectedWeekdays = new Set(weekdays);
              if (isSelected) selectedWeekdays.delete(weekday);
              else selectedWeekdays.add(weekday);
              onChange(NOVA_SCHEDULE_WEEKDAYS.filter((candidate) => selectedWeekdays.has(candidate)));
            }}
            className={cn(
              'border-steel-100 text-steel-400 hover:bg-wtw-infinity-100 size-10 min-w-10 rounded-full border bg-white p-0 text-[14px]',
              isSelected && ['text-wtw-ultraviolet-850 border-transparent', novaGradientClass.aiBackground],
            )}
          >
            {WEEKDAY_LABELS[weekday]}
          </Button>
        );
      })}
    </div>
  );
}

function parseTimeOfDay(value: string): ParsedTimeOfDay {
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  const hours = Math.min(23, Math.max(0, Number(match?.[1] ?? 9)));
  const minutes = Math.min(59, Math.max(0, Number(match?.[2] ?? 0)));

  return {
    clockValue: `${hours % 12 || 12}:${String(minutes).padStart(2, '0')}`,
    period: hours >= 12 ? 'PM' : 'AM',
  };
}

function toTwentyFourHourTime(clockValue: string, period: TimePeriod): string {
  const [hourValue = '12', minuteValue = '00'] = clockValue.split(':');
  const hour = (Number(hourValue) % 12) + (period === 'PM' ? 12 : 0);
  return `${String(hour).padStart(2, '0')}:${minuteValue}`;
}

function formatClockValue(minutesSinceMidnight: number): string {
  const hour = Math.floor(minutesSinceMidnight / 60) || 12;
  return `${hour}:${String(minutesSinceMidnight % 60).padStart(2, '0')}`;
}

function clockValueToMinutes(clockValue: string): number {
  const [hourValue = '12', minuteValue = '0'] = clockValue.split(':');
  return (Number(hourValue) % 12) * 60 + Number(minuteValue);
}

function formatOrdinal(value: number): string {
  const remainder = value % 100;
  if (remainder >= 11 && remainder <= 13) return `${value}th`;
  if (value % 10 === 1) return `${value}st`;
  if (value % 10 === 2) return `${value}nd`;
  if (value % 10 === 3) return `${value}rd`;
  return `${value}th`;
}

export function ScheduleField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid min-w-0 grid-cols-1 gap-1 sm:grid-cols-[74px_minmax(0,1fr)] sm:items-center sm:gap-4">
      <span className="text-steel-500 text-[12px] leading-4 font-semibold">{label}</span>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function formatDeliveryPolicy(outputPolicy: NovaScheduleOutputPolicy | undefined): string {
  return outputPolicy === 'NEW_CHAT_PER_RUN' ? 'Fresh chat thread' : 'Continuous chat thread';
}
